/* RareTCG mobile commerce ownership hardening.
 *
 * The core mobile shell is the single owner of Shop/Search/Cart navigation.
 * This file deliberately does NOT install a second hamburger click handler.
 * Its only responsibility is to make external-modal detection reflect actual
 * open UI instead of off-canvas/opacity-zero dialog markup left in the DOM by
 * Shopify or apps.
 */
(() => {
  if (window.__rtcgMobileCommercePolish) return;
  window.__rtcgMobileCommercePolish = true;

  const isActuallyVisible = (element) => {
    if (!(element instanceof Element) || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    if (element.closest('[inert]')) return false;

    if (typeof element.checkVisibility === 'function') {
      try {
        if (!element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
      } catch (_) {}
    }

    let node = element;
    while (node && node !== document.documentElement) {
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
      const opacity = Number.parseFloat(style.opacity);
      if (Number.isFinite(opacity) && opacity <= 0.01) return false;
      node = node.parentElement;
    }

    const rect = element.getBoundingClientRect();
    const viewport = window.visualViewport;
    const width = viewport?.width || window.innerWidth || document.documentElement.clientWidth;
    const height = viewport?.height || window.innerHeight || document.documentElement.clientHeight;

    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < height && rect.left < width;
  };

  const install = () => {
    const ShopController = window.RareTCGMobileShop;
    if (!ShopController?.prototype) return false;

    if (!ShopController.prototype.__rtcgExternalOwnerV2) {
      Object.defineProperty(ShopController.prototype, '__rtcgExternalOwnerV2', {
        value: true,
        configurable: false,
        enumerable: false
      });

      ShopController.prototype.hasExternalOwner = function hasExternalOwner() {
        const ownedByRareTCG = (element) => {
          if (!element) return false;
          if (element === this.dialog || this.dialog?.contains(element)) return true;
          return [...this.layers.values()].some(layer => element === layer.dialog || layer.dialog?.contains(element));
        };

        /* Explicit lifecycle states are authoritative. A closed
           pickup-availability-drawer is intentionally absent from this list;
           Shopify leaves that role=dialog node mounted off-canvas on PDPs. */
        const explicitSelectors = [
          'shopify-chat[open]',
          '.rtcg-facet-drawer.is-open',
          'cart-drawer.active',
          'cart-notification .cart-notification.active',
          'quick-add-modal[open]',
          'product-modal[open]',
          'pickup-availability-drawer[open]',
          '.rtcg-shipping-drawer[open]',
          '.timesact-modal.timesact-modal-open'
        ];

        if (document.body.classList.contains('rtcg-search-open')) return true;

        for (const selector of explicitSelectors) {
          const element = document.querySelector(selector);
          if (element && !ownedByRareTCG(element)) return true;
        }

        const smilePanels = [...document.querySelectorAll('#smile-ui-container .smile-panel-frame-container')];
        if (smilePanels.some(isActuallyVisible)) return true;

        const genericDialogs = [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], .MuiDialog-root')];
        if (genericDialogs.some(element => !ownedByRareTCG(element) && isActuallyVisible(element))) return true;

        return [...document.querySelectorAll('pandectes-cmp')].some(host => {
          const dialog = host.shadowRoot?.querySelector('dialog[open]');
          return dialog ? isActuallyVisible(dialog) : false;
        });
      };
    }

    /* Clear a stale false-positive ownership state immediately. This matters on
       PDPs where pickup availability may have appended its closed drawer before
       this hardening file executes. */
    const shop = document.querySelector('[data-rtcg-shop]')?.rtcgShop;
    if (shop) {
      const external = shop.hasExternalOwner();
      if (shop.externalOwner !== external) shop.syncOwnership(external);
      if (!external) {
        document.body.classList.remove('rtcg-shell-external-owner');
        const shell = document.querySelector('[data-rtcg-mobile-shell]');
        if (shell) shell.inert = false;
      }
    }

    return true;
  };

  let attempts = 0;
  const retry = () => {
    if (install() || attempts >= 20) return;
    attempts += 1;
    window.setTimeout(retry, 50);
  };

  retry();
  document.addEventListener('shopify:section:load', () => window.setTimeout(install, 0));
})();
